import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Typography,
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import StripeElements from '../components/payments/StripeElements';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { RECHARGE_PACKS, startRechargeCheckout } from '../utils/creditWalletRpc.js';
import { creditWalletOperationCompleted } from './WalletBalanceContext.js';
import { canRechargeCredits, getRechargeStatus, getRechargeUnavailableMessage } from '../utils/rechargeEligibility.js';

// Phase 6 recharge entry (plan 06 §5, decision D-014): the four allow-listed
// packs — 20/40/80/160 credits at HK$100/200/400/800 (flat HK$5/credit) —
// starting checkout via POST /api/recharge-checkout with the pack id only.
//
// The browser never reads a Stripe price id: the subscription backend
// derives the therapist from the verified session, resolves amount/price
// from the D-014 allow-list, and fails closed with 503 when recharge
// products are unconfigured. payment_intent.succeeded is the definitive
// grant event, so this surface never claims credits were added; the wallet
// refresh event is dispatched per the existing convention so the balance
// refetches when the server-confirmed grant lands.
//
// The publishable key is read from env exactly as the subscription checkout
// surfaces do (SubscriptionView / CreateSubscription); no key is hardcoded.

// The grant is asynchronous: the server confirms the PaymentIntent and only
// then does the payment_intent.succeeded webhook deposit the credits. So this
// nudges the balance chip to refetch on confirmation and again a couple of
// times after short delays, giving the webhook time to land without a manual
// reload. The Realtime subscription in WalletBalanceContext still applies the
// authoritative row the moment Postgres emits it.
const REFRESH_DELAYS_MS = [1500, 4000];
const scheduleWalletRefreshes = () => {
  creditWalletOperationCompleted();
  return REFRESH_DELAYS_MS.map((delay) => setTimeout(creditWalletOperationCompleted, delay));
};

// The card-entry + confirmation step. Rendered inside <StripeElements> once the
// backend has returned a PaymentIntent client secret for the chosen pack.
// Confirmation happens in-browser via the Payment Element; there is no
// success redirect (redirect: 'if_required'), matching the plan's "no
// optimistic credit from a redirect" rule.
const RechargePaymentForm = ({ pack, onPaid, onBack, canPay, paymentAllowed, onPayingChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isPaying, setIsPaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async (event) => {
    event.preventDefault();
    if (!canPay() || !stripe || !elements || !isReady || loadError || isPaying) return;
    setIsPaying(true);
    onPayingChange(true);
    setError(null);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        return;
      }
      if (!canPay()) {
        setError('Your paid plan is being checked. Wait for verification to finish, then press Pay again.');
        return;
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmError) {
        setError(confirmError.message);
        return;
      }
      // The PaymentIntent is confirmed. Credits are deposited by the
      // payment_intent.succeeded webhook, not here, so report success as
      // "on the way" and let the wallet refreshes catch the grant.
      if (paymentIntent && (paymentIntent.status === 'succeeded'
        || paymentIntent.status === 'processing')) {
        onPaid(pack);
        return;
      }
      // Any other terminal status (e.g. requires_payment_method) is a
      // failure the customer must act on.
      setError('Payment was not completed. Please try again.');
    } catch (err) {
      setError(err.message || 'Payment could not be completed.');
    } finally {
      setIsPaying(false);
      onPayingChange(false);
    }
  };

  return (
    <Box component="form" aria-label="Credit recharge payment" onSubmit={handlePay}>
      <DialogContentText sx={{ mb: 2 }}>
        Paying HK${pack.amountHkd} for {pack.credits} credits. Enter your card
        details to complete the purchase.
      </DialogContentText>
      <Box sx={{ mb: 2 }}>
        {!isReady && !loadError && <Typography role="status">Loading secure payment form…</Typography>}
        <PaymentElement
          onReady={() => { setIsReady(true); setLoadError(false); }}
          onLoadError={() => {
            setIsReady(false);
            setLoadError(true);
            setError('The payment form could not load. Go back and try again.');
          }}
          options={{
            wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          }}
        />
      </Box>
      {error && (
        <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1 }}>
          {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2 }}>
        <Button
          onClick={onBack}
          disabled={isPaying}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!paymentAllowed || !stripe || !elements || !isReady || loadError || isPaying}
          startIcon={isPaying ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{
            backgroundColor: '#29C0BB',
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#1FA09B' },
          }}
        >
          {isPaying ? 'Paying…' : `Pay HK$${pack.amountHkd}`}
        </Button>
      </Box>
    </Box>
  );
};

const BuyCreditsButton = ({ subscription, subscriptionLoading = false, subscriptionUnavailable = false, onViewPlans }) => {
  const [open, setOpen] = useState(false);
  const [busyPackId, setBusyPackId] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  // The card step is driven by the client secret the backend returns for the
  // chosen pack; while it is set the dialog shows the Payment Element.
  const [clientSecret, setClientSecret] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [paymentInFlight, setPaymentInFlight] = useState(false);
  const requestRef = useRef(0);
  const busyRef = useRef(false);
  const eligibilityRef = useRef({ subscription, subscriptionLoading, subscriptionUnavailable });
  eligibilityRef.current = { subscription, subscriptionLoading, subscriptionUnavailable };
  const rechargeStatus = subscriptionLoading ? 'loading' : subscriptionUnavailable ? 'unavailable' : getRechargeStatus(subscription);
  const eligible = rechargeStatus === 'eligible';
  const confirmedIneligible = !subscriptionLoading && !subscriptionUnavailable && rechargeStatus !== 'unavailable' && !eligible;
  const unavailableMessage = getRechargeUnavailableMessage(rechargeStatus);
  const canPay = () => !eligibilityRef.current.subscriptionLoading
    && !eligibilityRef.current.subscriptionUnavailable
    && canRechargeCredits(eligibilityRef.current.subscription);

  // A refresh is not a loss of eligibility. Keep the intent and card iframe
  // while verification is pending, and only invalidate confirmed ineligibility.
  useEffect(() => {
    if (!confirmedIneligible || paymentInFlight) return;
    if (clientSecret || busyPackId) {
      setError('Your current plan no longer allows new credit purchases.');
    }
    requestRef.current += 1;
    busyRef.current = false;
    setBusyPackId(null);
    setClientSecret(null);
    setSelectedPack(null);
  }, [confirmedIneligible, paymentInFlight, clientSecret, busyPackId]);

  useEffect(() => () => { requestRef.current += 1; }, []);

  const resetFlow = () => {
    requestRef.current += 1;
    busyRef.current = false;
    setBusyPackId(null);
    setError(null);
    setDone(null);
    setClientSecret(null);
    setSelectedPack(null);
  };

  const handleCheckout = async (pack) => {
    if (!canPay() || busyRef.current) return;
    busyRef.current = true;
    const requestId = ++requestRef.current;
    setBusyPackId(pack.packId);
    setError(null);
    setDone(null);
    try {
      // startRechargeCheckout returns the full backend payload as `data`; the
      // PaymentIntent client secret it carries is what the Payment Element
      // needs to collect and confirm the card in-browser.
      const { data, error: checkoutError } = await startRechargeCheckout(pack.packId);
      if (requestId !== requestRef.current) return;
      if (checkoutError) {
        setError(checkoutError);
        return;
      }
      if (!data?.clientSecret) {
        setError('Could not start the credit purchase.');
        return;
      }
      setSelectedPack(pack);
      setClientSecret(data.clientSecret);
    } catch (err) {
      if (requestId === requestRef.current) {
        setError(err.message || 'Could not start the credit purchase.');
      }
    } finally {
      if (requestId === requestRef.current) {
        busyRef.current = false;
        setBusyPackId(null);
      }
    }
  };

  const handlePaid = (pack) => {
    scheduleWalletRefreshes();
    setClientSecret(null);
    setSelectedPack(null);
    setDone(
      `Payment succeeded — your ${pack.credits} credits will appear shortly.`,
    );
  };

  const handleBack = () => {
    setClientSecret(null);
    setSelectedPack(null);
    setError(null);
  };

  const closeDialog = () => {
    setOpen(false);
    resetFlow();
  };

  const inCardStep = Boolean(clientSecret && selectedPack);

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddCardIcon />}
        disabled={!eligible}
        onClick={() => {
          if (!canPay()) return;
          setOpen(true);
          resetFlow();
        }}
        sx={{
          backgroundColor: '#29C0BB',
          color: '#FFFFFF',
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { backgroundColor: '#1FA09B' },
        }}
      >
        Buy credits
      </Button>
      {!subscriptionLoading && !eligible && <>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {unavailableMessage}
        </Typography>
        {onViewPlans && <Button onClick={onViewPlans} sx={{ mt: .5, textTransform: 'none' }}>View plans</Button>}
      </>}
      <Dialog
        open={open}
        onClose={() => !busyPackId && !paymentInFlight && closeDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Buy credits</DialogTitle>
        <DialogContent>
          {!eligible && <DialogContentText sx={{ mb: 2 }}>
            {subscriptionLoading ? 'Checking your paid plan…' : unavailableMessage}
          </DialogContentText>}
          {error && <Typography role="alert" variant="body2" sx={{ color: '#d32f2f', mt: 1.5 }}>{error}</Typography>}
          {done && <Typography role="status" variant="body2" sx={{ color: '#15803D', mt: 1.5 }}>{done}</Typography>}
          {inCardStep ? (
            <StripeElements
              key={clientSecret}
              options={{
                clientSecret,
                appearance: { theme: 'stripe', variables: { colorPrimary: '#29C0BB' } },
              }}
            >
              <RechargePaymentForm
                pack={selectedPack}
                onPaid={handlePaid}
                onBack={handleBack}
                canPay={canPay}
                paymentAllowed={eligible}
                onPayingChange={setPaymentInFlight}
              />
            </StripeElements>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                One-off packs add credits to your wallet at HK$5 per credit. Credits are
                added once your payment is confirmed.
              </DialogContentText>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {RECHARGE_PACKS.map((pack) => (
                  <Paper
                    key={pack.packId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {pack.credits} credits
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        HK${pack.amountHkd}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!eligible || busyPackId !== null}
                      startIcon={
                        busyPackId === pack.packId ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : undefined
                      }
                      onClick={() => handleCheckout(pack)}
                      sx={{
                        backgroundColor: '#29C0BB',
                        borderRadius: '8px',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#1FA09B' },
                      }}
                    >
                      {busyPackId === pack.packId ? 'Starting…' : 'Buy'}
                    </Button>
                  </Paper>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busyPackId !== null || paymentInFlight} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BuyCreditsButton;
